import { useState, useRef } from "react";
import { RefreshCw, Volume2, Loader2, Dumbbell, Square, Share2 } from "lucide-react";
import SwingGauge from "./SwingGauge";
import DataCard from "./DataCard";
import RadarChart from "./RadarChart";
import VideoPlayer from "./VideoPlayer";
import ShareModal from "./ShareModal";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const Dashboard = ({ data, onNewAnalysis }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const audioRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingQueueRef = useRef(false);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    audioQueueRef.current = [];
    isPlayingQueueRef.current = false;
    setIsPlaying(false);
  };

  const handleNewAnalysis = () => {
    stopAudio();
    onNewAnalysis();
  };

  // Split text into chunks by paragraph or sentence
  const chunkText = (text) => {
    const paragraphs = text.split(/\n\n+/);
    const chunks = [];
    for (const para of paragraphs) {
      if (para.length <= 600) {
        chunks.push(para);
      } else {
        // Split long paragraphs by sentence
        const sentences = para.match(/[^.!?]+[.!?]+/g) || [para];
        let current = '';
        for (const sentence of sentences) {
          if ((current + sentence).length <= 600) {
            current += sentence;
          } else {
            if (current) chunks.push(current.trim());
            current = sentence;
          }
        }
        if (current) chunks.push(current.trim());
      }
    }
    return chunks.filter(c => c.length > 0);
  };

  const playNextChunk = async () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingQueueRef.current = false;
      setIsPlaying(false);
      return;
    }

    const chunk = audioQueueRef.current.shift();
    try {
      const response = await axios.post(`${BACKEND_URL}/api/tts/chunk`, { text: chunk });
      
      if (response.data.success && isPlayingQueueRef.current) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
        audioRef.current = audio;
        audio.onended = () => playNextChunk();
        audio.play();
      }
    } catch (err) {
      console.error('TTS chunk error:', err);
      playNextChunk(); // Try next chunk
    }
  };

  const playCoachAnalysis = async () => {
    if (isPlaying || audioLoading) return;
    
    setAudioLoading(true);
    const chunks = chunkText(data.coach_analysis);
    audioQueueRef.current = chunks.slice(1); // Queue all but first
    isPlayingQueueRef.current = true;
    
    try {
      // Start with first chunk immediately
      const response = await axios.post(`${BACKEND_URL}/api/tts/chunk`, { text: chunks[0] });
      
      if (response.data.success) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
        audioRef.current = audio;
        audio.onended = () => playNextChunk();
        audio.onplay = () => setIsPlaying(true);
        setAudioLoading(false);
        audio.play();
      } else {
        setAudioLoading(false);
        isPlayingQueueRef.current = false;
      }
    } catch (err) {
      console.error('TTS error:', err);
      setAudioLoading(false);
      isPlayingQueueRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="dashboard">
      {/* Header */}
      <header className="border-b border-[#262626] px-4 md:px-12 py-4 md:py-6">
        <div className="flex flex-col gap-3">
          {/* Top row: Logo - centered on mobile, left on desktop */}
          <div className="flex items-center justify-center sm:justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://rezzil.com/wp-content/uploads/2023/11/Rezzil-Logo-White-800.png" 
                alt="Rezzil" 
                className="h-6 md:h-8"
              />
              <span className="font-heading text-lg md:text-xl tracking-wider text-[#A3A3A3] uppercase">
                Pro-Shot
              </span>
            </div>
            {/* Desktop buttons */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-2 border transition-colors rounded-sm px-4 py-2 text-sm uppercase tracking-wider font-bold bg-[#CCFF00] border-[#CCFF00] text-black hover:bg-white hover:border-white"
                data-testid="share-button"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
              <button
                onClick={handleNewAnalysis}
                className="flex items-center gap-2 bg-transparent border border-[#262626] text-white hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors rounded-sm px-4 py-2 text-sm uppercase tracking-wider font-bold"
                data-testid="new-analysis-button"
              >
                <RefreshCw className="w-4 h-4" />
                New Analysis
              </button>
            </div>
          </div>
          {/* Mobile buttons - below logo, centered */}
          <div className="flex sm:hidden items-center justify-center gap-2 mt-1">
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-1.5 border transition-colors rounded-sm px-3 py-1.5 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] border-[#CCFF00] text-black"
              data-testid="share-button-mobile"
            >
              <Share2 className="w-3 h-3" />
              Share
            </button>
            <button
              onClick={handleNewAnalysis}
              className="flex items-center gap-1.5 bg-transparent border border-[#262626] text-white transition-colors rounded-sm px-3 py-1.5 text-xs uppercase tracking-wider font-bold"
              data-testid="new-analysis-button-mobile"
            >
              <RefreshCw className="w-3 h-3" />
              New
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 md:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          
          {/* DESKTOP LAYOUT: Video left, 2x4 grid right */}
          <section className="hidden lg:block mb-6 animate-fade-in" data-testid="desktop-layout">
            <div className="grid grid-cols-12 gap-3">
              {/* Video Player - Left side */}
              {data.videoUrl && (
                <div className="col-span-4">
                  <VideoPlayer videoUrl={data.videoUrl} analysisData={data} />
                </div>
              )}
              
              {/* 2x4 Grid of Stats - Right side */}
              <div className={data.videoUrl ? "col-span-8" : "col-span-12"}>
                <div className="grid grid-cols-4 gap-3">
                  {/* Row 1 */}
                  <div className="bg-[#121212] border border-[#262626] p-3 rounded-sm flex flex-col aspect-square">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1 text-center">Overall Grade</p>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <SwingGauge score={data.overall_grade} size={60} />
                      <p className="font-heading font-black text-xl text-white">{data.overall_grade}</p>
                    </div>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-3 rounded-sm flex flex-col aspect-square">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1 text-center">Swing Path</p>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <SemiCircleGauge angle={data.swing_path_angle} status={data.swing_path_status} />
                    </div>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-3 rounded-sm flex flex-col aspect-square">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1 text-center">Impact Position</p>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <StatusBadge status={data.impact_position} />
                    </div>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-3 rounded-sm flex flex-col aspect-square">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1 text-center">Kinetic Sequence</p>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <PowerBar value={data.kinetic_sequence} />
                    </div>
                  </div>
                  
                  {/* Row 2 */}
                  <div className="bg-[#121212] border border-[#262626] p-3 rounded-sm flex flex-col aspect-square">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1 text-center">Tempo & Timing</p>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <RatioDisplay ratio={data.tempo_ratio} />
                    </div>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-3 rounded-sm flex flex-col aspect-square">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1 text-center">Balance</p>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <RadarChart data={{ front: data.balance_front, back: data.balance_back, left: data.balance_left, right: data.balance_right }} />
                    </div>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-3 rounded-sm flex flex-col aspect-square">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1 text-center">Backswing Time</p>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <p className="font-heading font-black text-2xl text-[#CCFF00]">
                        {data.phase_timestamps?.top_of_swing && data.phase_timestamps?.backswing_start 
                          ? (data.phase_timestamps.top_of_swing - data.phase_timestamps.backswing_start).toFixed(2)
                          : '--'}
                      </p>
                      <p className="font-mono text-xs text-[#A3A3A3]">Seconds</p>
                    </div>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-3 rounded-sm flex flex-col aspect-square">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1 text-center">Downswing Time</p>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <p className="font-heading font-black text-2xl text-[#CCFF00]">
                        {data.phase_timestamps?.impact && data.phase_timestamps?.downswing_start
                          ? (data.phase_timestamps.impact - data.phase_timestamps.downswing_start).toFixed(2)
                          : '--'}
                      </p>
                      <p className="font-mono text-xs text-[#A3A3A3]">Seconds</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* MOBILE/TABLET LAYOUT: Same 8 stats as desktop */}
          <section className="lg:hidden mb-6 animate-fade-in" data-testid="mobile-layout">
            {/* Video */}
            {data.videoUrl && (
              <div className="mb-6">
                <VideoPlayer videoUrl={data.videoUrl} analysisData={data} />
              </div>
            )}

            {/* Same 8 Stats as Desktop - 2 column grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#121212] border border-[#262626] p-4 rounded-sm flex flex-col">
                <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2 text-center">Overall Grade</p>
                <div className="flex-1 flex flex-col items-center justify-center">
                  <SwingGauge score={data.overall_grade} size={80} />
                  <p className="font-heading font-black text-2xl text-white">{data.overall_grade}</p>
                </div>
              </div>
              <div className="bg-[#121212] border border-[#262626] p-4 rounded-sm flex flex-col">
                <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2 text-center">Swing Path</p>
                <div className="flex-1 flex flex-col items-center justify-center">
                  <SemiCircleGauge angle={data.swing_path_angle} status={data.swing_path_status} />
                </div>
              </div>
              <div className="bg-[#121212] border border-[#262626] p-4 rounded-sm flex flex-col">
                <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2 text-center">Impact Position</p>
                <div className="flex-1 flex flex-col items-center justify-center">
                  <StatusBadge status={data.impact_position} />
                </div>
              </div>
              <div className="bg-[#121212] border border-[#262626] p-4 rounded-sm flex flex-col">
                <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2 text-center">Kinetic Sequence</p>
                <div className="flex-1 flex flex-col items-center justify-center">
                  <PowerBar value={data.kinetic_sequence} />
                </div>
              </div>
              <div className="bg-[#121212] border border-[#262626] p-4 rounded-sm flex flex-col">
                <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2 text-center">Tempo & Timing</p>
                <div className="flex-1 flex flex-col items-center justify-center">
                  <RatioDisplay ratio={data.tempo_ratio} />
                </div>
              </div>
              <div className="bg-[#121212] border border-[#262626] p-4 rounded-sm flex flex-col">
                <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2 text-center">Balance</p>
                <div className="flex-1 flex flex-col items-center justify-center">
                  <RadarChart data={{ front: data.balance_front, back: data.balance_back, left: data.balance_left, right: data.balance_right }} />
                </div>
              </div>
              <div className="bg-[#121212] border border-[#262626] p-4 rounded-sm flex flex-col">
                <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2 text-center">Backswing Time</p>
                <div className="flex-1 flex flex-col items-center justify-center">
                  <p className="font-heading font-black text-3xl text-[#CCFF00]">
                    {data.phase_timestamps?.top_of_swing && data.phase_timestamps?.backswing_start 
                      ? (data.phase_timestamps.top_of_swing - data.phase_timestamps.backswing_start).toFixed(2)
                      : '--'}
                  </p>
                  <p className="font-mono text-xs text-[#A3A3A3] mt-1">Seconds</p>
                </div>
              </div>
              <div className="bg-[#121212] border border-[#262626] p-4 rounded-sm flex flex-col">
                <p className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2 text-center">Downswing Time</p>
                <div className="flex-1 flex flex-col items-center justify-center">
                  <p className="font-heading font-black text-3xl text-[#CCFF00]">
                    {data.phase_timestamps?.impact && data.phase_timestamps?.downswing_start
                      ? (data.phase_timestamps.impact - data.phase_timestamps.downswing_start).toFixed(2)
                      : '--'}
                  </p>
                  <p className="font-mono text-xs text-[#A3A3A3] mt-1">Seconds</p>
                </div>
              </div>
            </div>
          </section>

          {/* Coach's Technical Analysis */}
          <section className="animate-fade-in mb-6" data-testid="coach-analysis-section">
            <div className="bg-[#121212] border border-[#262626] rounded-sm p-6 md:p-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-xl uppercase tracking-tight text-[#CCFF00]">
                  Coach's Technical Analysis
                </h2>
                <div className="flex items-center gap-2">
                  {isPlaying ? (
                    <button
                      onClick={stopAudio}
                      className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors bg-[#FF3B30] text-white hover:bg-[#ff5247] border border-[#FF3B30]"
                      data-testid="stop-button"
                    >
                      <Square className="w-4 h-4" />
                      Stop
                    </button>
                  ) : (
                    <button
                      onClick={playCoachAnalysis}
                      disabled={audioLoading}
                      className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors bg-[#1A1A1A] text-[#A3A3A3] hover:text-[#CCFF00] hover:border-[#CCFF00] border border-[#262626]"
                      data-testid="listen-button"
                    >
                      {audioLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Volume2 className="w-4 h-4" />
                      )}
                      Listen
                    </button>
                  )}
                </div>
              </div>
              <div className="coach-analysis text-sm md:text-base whitespace-pre-line">
                {data.coach_analysis}
              </div>
            </div>
          </section>

          {/* Practice Plan */}
          {data.practice_plan && (
            <section className="animate-fade-in" data-testid="practice-plan-section">
              <div className="bg-[#121212] border border-[#262626] rounded-sm p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Dumbbell className="w-6 h-6 text-[#CCFF00]" />
                  <h2 className="font-heading font-bold text-xl uppercase tracking-tight text-[#CCFF00]">
                    Your Practice Plan
                  </h2>
                </div>
                
                {/* Focus Area */}
                <div className="mb-6 p-4 bg-[#1A1A1A] rounded-sm border-l-4 border-[#CCFF00]">
                  <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mb-1">Focus Area</p>
                  <p className="text-white font-medium">{data.practice_plan.focus_area}</p>
                </div>

                {/* Drills */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {data.practice_plan.drills?.map((drill, index) => (
                    <div key={index} className="bg-[#1A1A1A] rounded-sm p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-[#CCFF00] text-black flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </span>
                        <h3 className="font-heading font-bold text-white uppercase">{drill.name}</h3>
                      </div>
                      <p className="text-[#A3A3A3] text-sm mb-3">{drill.description}</p>
                      <div className="flex justify-between text-xs">
                        <span className="text-[#CCFF00] font-mono">{drill.reps}</span>
                      </div>
                      <p className="text-[#666] text-xs mt-2 italic">{drill.purpose}</p>
                    </div>
                  ))}
                </div>

                {/* Weekly Schedule */}
                <div className="p-4 bg-[#1A1A1A] rounded-sm">
                  <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mb-1">Weekly Schedule</p>
                  <p className="text-white text-sm">{data.practice_plan.weekly_schedule}</p>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
        analysisId={data.id}
        grade={data.overall_grade}
      />
    </div>
  );
};

const QuickStat = ({ label, value, highlight }) => (
  <div className="bg-[#121212] border border-[#262626] p-3 rounded-sm">
    <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
      {label}
    </p>
    <p className={`font-heading font-bold text-lg uppercase ${highlight ? 'text-[#CCFF00]' : 'text-white'}`}>
      {value}
    </p>
  </div>
);

const SemiCircleGauge = ({ angle, status }) => {
  // Map angle from -45 to 45 to percentage (0 to 100)
  const percentage = ((angle + 45) / 90) * 100;
  const rotation = (percentage / 100) * 180 - 90;

  return (
    <div className="flex flex-col items-center justify-center h-full">
      <svg viewBox="0 0 120 70" className="w-full max-w-[120px]">
        {/* Background arc */}
        <path
          d="M 10 60 A 50 50 0 0 1 110 60"
          fill="none"
          stroke="#262626"
          strokeWidth="8"
          strokeLinecap="round"
        />
        {/* Labels */}
        <text x="5" y="68" fill="#A3A3A3" fontSize="8" fontFamily="JetBrains Mono">IN</text>
        <text x="55" y="12" fill="#A3A3A3" fontSize="8" fontFamily="JetBrains Mono" textAnchor="middle">SQ</text>
        <text x="108" y="68" fill="#A3A3A3" fontSize="8" fontFamily="JetBrains Mono" textAnchor="end">OUT</text>
        {/* Needle */}
        <line
          x1="60"
          y1="60"
          x2="60"
          y2="20"
          stroke="#CCFF00"
          strokeWidth="3"
          strokeLinecap="round"
          transform={`rotate(${rotation}, 60, 60)`}
        />
        {/* Center dot */}
        <circle cx="60" cy="60" r="5" fill="#CCFF00" />
      </svg>
      <p className="font-heading font-bold text-lg text-white mt-2 uppercase">
        {status}
      </p>
      <p className="font-mono text-xs text-[#A3A3A3]">
        {angle > 0 ? '+' : ''}{angle.toFixed(1)}°
      </p>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusClass = status.toLowerCase() === 'square' ? 'square' : 
                      status.toLowerCase() === 'open' ? 'open' : 'closed';
  
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className={`status-badge ${statusClass}`}>
        {status}
      </div>
      <p className="font-mono text-xs text-[#A3A3A3] mt-3 uppercase">
        Clubface at Impact
      </p>
    </div>
  );
};

const PowerBar = ({ value }) => (
  <div className="flex flex-col justify-center h-full">
    <div className="flex items-end justify-between mb-2">
      <span className="font-heading font-black text-4xl text-white">{value}</span>
      <span className="font-mono text-xs text-[#A3A3A3] uppercase">/ 100</span>
    </div>
    <div className="power-bar">
      <div 
        className="power-bar-fill" 
        style={{ width: `${value}%` }}
      />
    </div>
    <p className="font-mono text-xs text-[#A3A3A3] mt-3 uppercase">
      Power Transfer Efficiency
    </p>
  </div>
);

const RatioDisplay = ({ ratio }) => {
  const parts = ratio.split(':');
  
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="ratio-display flex items-baseline">
        <span className="text-white">{parts[0]}</span>
        <span className="ratio-separator">:</span>
        <span className="text-white">{parts[1]}</span>
      </div>
      <p className="font-mono text-xs text-[#A3A3A3] mt-2 uppercase">
        Backswing : Downswing
      </p>
    </div>
  );
};

export default Dashboard;
